import { createTheme } from '@mui/material/styles'
import { deepmerge } from '@mui/utils'

// CHOICES:
const palette = {
  black: '#000',
  white: '#fff',
  lightGray: '#f5f5f5',
  gray: '#e0e0e0',
  darkGray: '#333',
  // alert colors
  green: '#347433',
  yellow: '#eebf34ff',
  red: '#B22222',
  // brand colors
  teal: '#196B81',
  orange: '#E47633',
  background: '#FFFFFF',
  cream: '#E9E2D3'
}

const fontFamily = {
  main: ['archer-pro', 'serif'].join(', '),
  subtitles: ['poppins', 'sans-serif'].join(', '),
  accents: ['calder-lc', 'sans-serif'].join(', ')
}

// Standard font weights:
// font-weight: 100; /* Thin */
// font-weight: 200; /* Extra Light */
// font-weight: 300; /* Light */
// font-weight: 400; /* Normal (default) */
// font-weight: 500; /* Medium */
// font-weight: 600; /* Semi Bold */
// font-weight: 700; /* Bold */
// font-weight: 800; /* Extra Bold */
// font-weight: 900; /* Black */

// 1. create base theme
let theme = createTheme({
  typography: {
    fontFamily: fontFamily.main
  },
  breakpoints: {
    // material-ui defaults:
    // xs, extra-small: 0px
    // sm, small: 600px
    // md, medium: 900px
    // lg, large: 1200px
    // xl, extra-large: 1536px
  },
  palette: {
    // color intentions:
    primary: {
      main: palette.teal,
      contrastText: palette.white
    },
    secondary: {
      main: palette.orange,
      contrastText: palette.white
    },
    success: {
      main: palette.green,
      contrastText: palette.white
    },
    info: {
      main: palette.gray
    },
    warning: {
      main: palette.yellow
    },
    error: {
      main: palette.red
    },
    background: {
      default: palette.lightGray
    }
  }
})

// 2. extend theme
theme = createTheme(theme, {
  typography: {
    h1: {
      color: theme.palette.primary.main,
      fontWeight: 700,
      marginTop: 0,
      marginBottom: '.6rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '24px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('sm')]: {
        fontSize: '30px',
        lineHeight: 1.125
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '39px',
        lineHeight: 1.125
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '44px',
        lineHeight: 1.125
      }
    },
    h2: {
      color: theme.palette.primary.main,
      fontWeight: 600,
      [theme.breakpoints.down('sm')]: {
        fontSize: '26px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('sm')]: {
        fontSize: '28px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '32px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '36px',
        lineHeight: 1.125
      }
    },
    h3: {
      color: theme.palette.primary.main,
      fontWeight: 400,
      [theme.breakpoints.down('md')]: {
        fontSize: '20px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '22px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '26px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '28px',
        lineHeight: 1.125
      }
    },
    h4: {
      color: theme.palette.primary.main,
      fontWeight: 400,
      [theme.breakpoints.down('md')]: {
        fontSize: '18px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '20px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '22px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '26px',
        lineHeight: 1.125
      }
    },
    h5: {
      color: theme.palette.primary.main,
      fontWeight: 400,
      [theme.breakpoints.down('md')]: {
        fontSize: '16px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '18px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '20px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '22px',
        lineHeight: 1.125
      }
    },
    h6: {
      color: theme.palette.primary.main,
      fontWeight: 400,
      [theme.breakpoints.down('md')]: {
        fontSize: '12px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '12px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '14px',
        lineHeight: 1.25
      },
      [theme.breakpoints.up('xl')]: {
        fontSize: '14px',
        lineHeight: 1.125
      }
    },
    subtitle1: {
      fontFamily: fontFamily.subtitles,
      fontWeight: 600
    },
    subtitle2: {
      fontFamily: fontFamily.subtitles,
      fontWeight: 600
    },
    body1: {
      // uno/variables/typography.scss
      fontWeight: 300,
      fontStyle: 'normal',
      [theme.breakpoints.down('lg')]: {
        fontSize: '18px',
        lineHeight: 1.3
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '20px',
        lineHeight: 1.3
      }
    },
    body2: {
      fontWeight: 300,
      fontStyle: 'normal'
    },
    button: {
      [theme.breakpoints.down('sm')]: {
        fontSize: '14px'
      },
      [theme.breakpoints.up('sm')]: {
        fontSize: '16px'
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '18px'
      }
    },
    caption: {},
    overline: {}
  },
  shape: {
    borderRadius: 20
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          lineHeight: 1.3
        },
        filledSuccess: {
          color: 'white'
        }
      }
    },
    AccountButton: {
      fontStyle: 'normal'
    },
    DesktopHeader: {
      Box: {
        bgcolor: palette.cream
      },
      Typography: {
        color: theme.palette.primary.main
      }
    },
    AccountFooter: {
      Box: {
        bgcolor: palette.cream,
        color: theme.palette.text.primary
      }
    }
  }
})

export default theme
